import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const FORMA_PAGAMENTO_OPCOES = [
  'À vista',
  'PIX',
  'Transferência',
  'Cartão de crédito',
  'Cartão de débito',
  'Financiamento',
  'Consórcio',
  'Outros',
];

const getFormaPagamentoSelecionada = (venda, selecionadas, outros) => {
  const hasSelecionada = Array.isArray(selecionadas) && selecionadas.length > 0;
  if (!hasSelecionada) {
    return venda?.forma_pagamento || '';
  }

  const normalized = selecionadas.filter((item) => item !== 'Outros');
  if (selecionadas.includes('Outros') && outros?.trim()) {
    normalized.push(outros.trim());
  }

  return normalized.join(' + ');
};

export default function Documentos() {
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [empresa, setEmpresa] = useState({});

  const [openContrato, setOpenContrato] = useState(false);
  const [selectedVenda, setSelectedVenda] = useState(null);
  const [formasSelecionadas, setFormasSelecionadas] = useState([]);
  const [formaOutros, setFormaOutros] = useState('');
  const [observacoesContrato, setObservacoesContrato] = useState('');

  const [openProcuracao, setOpenProcuracao] = useState(false);
  const [procuracaoData, setProcuracaoData] = useState({
    outorgante_id: null,
    veiculo_id: null,
    data_procuracao: new Date().toISOString().split('T')[0],
    cidade_registro: '',
    tipo_veiculo: 'PASSAGEIRO AUTOMÓVEL',
  });
  const [outorgado2Enabled, setOutorgado2Enabled] = useState(false);
  const [outorgado2, setOutorgado2] = useState({
    nome: '', cpf: '', rg: '', nacionalidade: 'BRASILEIRA',
    endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  });
  const [selectedOutorgante, setSelectedOutorgante] = useState(null);
  const [selectedVeiculoProcuracao, setSelectedVeiculoProcuracao] = useState(null);
  const [outorgadosDisponiveis, setOutorgadosDisponiveis] = useState([]);
  const [outorgadoSelecionado, setOutorgadoSelecionado] = useState(null);

  const [openRecibo, setOpenRecibo] = useState(false);
  const [selectedVendaRecibo, setSelectedVendaRecibo] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location.state?.gerarDocumento && location.state?.vendaId && vendas.length > 0) {
      const venda = vendas.find((item) => String(item.id) === String(location.state.vendaId));
      if (venda) {
        setSelectedVenda(venda);
        setOpenContrato(true);
      }

      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate, vendas]);

  const loadData = async () => {
    try {
      const [vendasResponse, clientesResponse, veiculosResponse] = await Promise.all([
        api.get('/vendas'),
        api.get('/clientes', { params: { limit: 1000 } }),
        api.get('/veiculos', { params: { limit: 1000 } }),
      ]);

      const vendasData = Array.isArray(vendasResponse.data) ? vendasResponse.data : [];
      const clientesData = clientesResponse.data?.clientes || clientesResponse.data || [];
      const veiculosData = veiculosResponse.data?.veiculos || veiculosResponse.data || [];

      setVendas(vendasData);
      setClientes(Array.isArray(clientesData) ? clientesData : []);
      setVeiculos(Array.isArray(veiculosData) ? veiculosData : []);
      try {
        const empresaResponse = await api.get('/configuracoes/empresa');
        setEmpresa(empresaResponse.data || {});
      } catch (error) {
        setEmpresa({});
      }
    } catch (error) {
      console.error('Erro ao carregar dados de documentos:', error);
      toast.error('Erro ao carregar dados para geração de documentos');
      setVendas([]);
      setClientes([]);
      setVeiculos([]);
      setEmpresa({});
    }
  };

  const downloadPdf = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    window.open(fullUrl, '_blank');
  };

  const vendaResumo = useMemo(() => {
    if (!selectedVenda) return null;

    const valor = Number(selectedVenda.valor_venda || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

    return {
      cliente: selectedVenda.cliente?.nome || '-',
      veiculo: selectedVenda.veiculo
        ? `${selectedVenda.veiculo.marca || ''} ${selectedVenda.veiculo.modelo || ''} - ${selectedVenda.veiculo.placa || '-'}`.trim()
        : '-',
      valor,
      data: selectedVenda.data_venda
        ? new Date(selectedVenda.data_venda).toLocaleDateString('pt-BR')
        : '-',
      forma_pagamento: selectedVenda.forma_pagamento || '-',
    };
  }, [selectedVenda]);

  const handleOpenContrato = () => {
    setSelectedVenda(null);
    setFormasSelecionadas([]);
    setFormaOutros('');
    setObservacoesContrato('');
    setOpenContrato(true);
  };

  const handleGerarContrato = async () => {
    if (!selectedVenda?.id) {
      toast.error('Selecione uma venda já realizada para gerar o contrato');
      return;
    }

    const formaPagamentoFinal = getFormaPagamentoSelecionada(
      selectedVenda,
      formasSelecionadas,
      formaOutros
    );

    try {
      setLoading(true);
      const response = await api.post('/documentos/gerar', {
        venda_id: selectedVenda.id,
        tipo: 'contrato',
        forma_pagamento: formaPagamentoFinal,
        observacoes: observacoesContrato,
      });

      if (response.data?.url) {
        downloadPdf(response.data.url);
        toast.success('Contrato gerado com sucesso');
      }
      setOpenContrato(false);
    } catch (error) {
      console.error('Erro ao gerar contrato:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar contrato');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenProcuracao = async () => {
    setProcuracaoData({
      outorgante_id: null,
      veiculo_id: null,
      data_procuracao: new Date().toISOString().split('T')[0],
      cidade_registro: '',
      tipo_veiculo: 'PASSAGEIRO AUTOMÓVEL',
    });
    setOutorgado2Enabled(false);
    setOutorgado2({ nome: '', cpf: '', rg: '', nacionalidade: 'BRASILEIRA', endereco: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '' });
    setSelectedOutorgante(null);
    setSelectedVeiculoProcuracao(null);
    setOutorgadoSelecionado(null);
    try {
      const resp = await api.get('/configuracoes/outorgados');
      const ativos = (resp.data || []).filter((o) => o.ativo);
      setOutorgadosDisponiveis(ativos);
      if (ativos.length === 1) setOutorgadoSelecionado(ativos[0]);
    } catch {
      setOutorgadosDisponiveis([]);
    }
    setOpenProcuracao(true);
  };

  const handleGerarProcuracao = async () => {
    try {
      if (!procuracaoData.outorgante_id) {
        toast.error('Selecione o outorgante');
        return;
      }
      if (!procuracaoData.veiculo_id) {
        toast.error('Selecione o veículo');
        return;
      }
      if (!outorgadoSelecionado) {
        toast.error('Selecione o outorgado');
        return;
      }

      setLoading(true);
      const payload = {
        ...procuracaoData,
        outorgado_selecionado: outorgadoSelecionado,
        outorgado2: outorgado2Enabled && outorgado2.nome ? outorgado2 : undefined,
      };
      const response = await api.post('/documentos/gerar-procuracao', payload);
      if (response.data?.url) {
        downloadPdf(response.data.url);
        toast.success('Procuração gerada com sucesso');
      }
      setOpenProcuracao(false);
    } catch (error) {
      console.error('Erro ao gerar procuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar procuração');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRecibo = () => {
    setSelectedVendaRecibo(null);
    setOpenRecibo(true);
  };

  const handleGerarRecibo = async () => {
    try {
      if (!selectedVendaRecibo?.id) {
        toast.error('Selecione uma venda já realizada');
        return;
      }

      setLoading(true);
      const response = await api.post('/documentos/gerar', {
        venda_id: selectedVendaRecibo.id,
        tipo: 'recibo',
      });

      if (response.data?.url) {
        downloadPdf(response.data.url);
        toast.success('Recibo gerado com sucesso');
      }
      setOpenRecibo(false);
    } catch (error) {
      console.error('Erro ao gerar recibo:', error);
      toast.error(error.response?.data?.error || 'Erro ao gerar recibo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Geração de Documentos
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Para contrato e recibo, selecione uma venda já realizada para preencher automaticamente os dados.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6">Contrato de Compra e Venda</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Selecione uma venda concluída e gere o contrato com dados automáticos.
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="contained" fullWidth onClick={handleOpenContrato} startIcon={<DescriptionIcon />}>
                Gerar Contrato
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AssignmentIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Typography variant="h6">Procuração</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Outorgado fixo como empresa cadastrada em configurações.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="warning"
                fullWidth
                onClick={handleOpenProcuracao}
                startIcon={<AssignmentIcon />}
              >
                Gerar Procuração
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReceiptIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Typography variant="h6">Recibo</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Gere recibo com base em venda concluída e dados da empresa.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={handleOpenRecibo}
                startIcon={<ReceiptIcon />}
              >
                Gerar Recibo
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openContrato} onClose={() => setOpenContrato(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar Contrato por Venda Realizada</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={vendas}
                value={selectedVenda}
                onChange={(event, newValue) => {
                  setSelectedVenda(newValue);
                  setFormasSelecionadas([]);
                  setFormaOutros('');
                }}
                getOptionLabel={(option) => {
                  const cliente = option.cliente?.nome || 'Cliente';
                  const veiculo = option.veiculo
                    ? `${option.veiculo.marca || ''} ${option.veiculo.modelo || ''} ${option.veiculo.placa ? `- ${option.veiculo.placa}` : ''}`.trim()
                    : 'Veículo';
                  return `#${option.id} - ${cliente} - ${veiculo}`;
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Venda já realizada *" fullWidth />
                )}
              />
            </Grid>

            {vendaResumo && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Resumo da venda</Typography>
                  <Typography variant="body2"><strong>Cliente:</strong> {vendaResumo.cliente}</Typography>
                  <Typography variant="body2"><strong>Veículo:</strong> {vendaResumo.veiculo}</Typography>
                  <Typography variant="body2"><strong>Valor:</strong> {vendaResumo.valor}</Typography>
                  <Typography variant="body2"><strong>Data:</strong> {vendaResumo.data}</Typography>
                  <Typography variant="body2"><strong>Pagamento salvo:</strong> {vendaResumo.forma_pagamento}</Typography>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Forma de pagamento no contrato (se não selecionar, usa a forma salva na venda)
              </Typography>
              <Autocomplete
                multiple
                options={FORMA_PAGAMENTO_OPCOES}
                value={formasSelecionadas}
                onChange={(event, values) => setFormasSelecionadas(values)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} key={option} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Selecione as formas" placeholder="Escolher" />
                )}
              />
            </Grid>

            {formasSelecionadas.includes('Outros') && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Outros (descreva)"
                  value={formaOutros}
                  onChange={(e) => setFormaOutros(e.target.value)}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Observações / cláusulas adicionais (opcional)"
                value={observacoesContrato}
                onChange={(e) => setObservacoesContrato(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenContrato(false)}>Cancelar</Button>
          <Button onClick={handleGerarContrato} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <PdfIcon />}>
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openProcuracao} onClose={() => setOpenProcuracao(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar Procuração</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {/* Outorgado */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Outorgado (quem recebe os poderes)</Typography>
              {outorgadosDisponiveis.length === 0 ? (
                <Alert severity="warning">
                  Nenhum outorgado ativo. Configure em <strong>Configurações → Outorgados</strong>.
                </Alert>
              ) : (
                <TextField
                  fullWidth
                  select
                  required
                  label="Selecione o Outorgado *"
                  value={outorgadoSelecionado?.id || ''}
                  onChange={(e) => {
                    const sel = outorgadosDisponiveis.find((o) => o.id === e.target.value);
                    setOutorgadoSelecionado(sel || null);
                  }}
                >
                  {outorgadosDisponiveis.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.tipo === 'empresa' ? `🏢 Empresa (${empresa?.razao_social || 'não configurada'})` : `👤 ${o.nome}${o.cpf ? ` — CPF ${o.cpf}` : ''}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>

            {/* Outorgante */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary">Outorgante (quem assina)</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={clientes}
                value={selectedOutorgante}
                onChange={(event, newValue) => {
                  setSelectedOutorgante(newValue);
                  setProcuracaoData((prev) => ({ ...prev, outorgante_id: newValue?.id || null }));
                }}
                getOptionLabel={(option) => `${option.nome} - ${option.cpf_cnpj || 'Sem CPF/CNPJ'}`}
                renderInput={(params) => <TextField {...params} label="Outorgante *" fullWidth />}
              />
            </Grid>

            {/* Veículo */}
            <Grid item xs={12}>
              <Divider />
              <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>Veículo</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={veiculos}
                value={selectedVeiculoProcuracao}
                onChange={(event, newValue) => {
                  setSelectedVeiculoProcuracao(newValue);
                  setProcuracaoData((prev) => ({ ...prev, veiculo_id: newValue?.id || null }));
                }}
                getOptionLabel={(option) => `${option.marca} ${option.modelo} - ${option.placa || '-'}`}
                renderInput={(params) => <TextField {...params} label="Veículo *" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cidade de registro (ex: TAPERA/RS)"
                value={procuracaoData.cidade_registro}
                onChange={(e) => setProcuracaoData((prev) => ({ ...prev, cidade_registro: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tipo do veículo"
                value={procuracaoData.tipo_veiculo}
                onChange={(e) => setProcuracaoData((prev) => ({ ...prev, tipo_veiculo: e.target.value }))}
              />
            </Grid>

            {/* Segundo outorgado */}
            <Grid item xs={12}>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="subtitle2" color="primary">Segundo outorgado (opcional)</Typography>
                <Button
                  size="small"
                  variant={outorgado2Enabled ? 'contained' : 'outlined'}
                  onClick={() => setOutorgado2Enabled((v) => !v)}
                >
                  {outorgado2Enabled ? 'Remover' : 'Adicionar'}
                </Button>
              </Box>
            </Grid>

            {outorgado2Enabled && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nome *" value={outorgado2.nome} onChange={(e) => setOutorgado2((p) => ({ ...p, nome: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Nacionalidade" value={outorgado2.nacionalidade} onChange={(e) => setOutorgado2((p) => ({ ...p, nacionalidade: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="CPF" value={outorgado2.cpf} onChange={(e) => setOutorgado2((p) => ({ ...p, cpf: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="RG" value={outorgado2.rg} onChange={(e) => setOutorgado2((p) => ({ ...p, rg: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Número" value={outorgado2.numero} onChange={(e) => setOutorgado2((p) => ({ ...p, numero: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField fullWidth label="Endereço" value={outorgado2.endereco} onChange={(e) => setOutorgado2((p) => ({ ...p, endereco: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Complemento" value={outorgado2.complemento} onChange={(e) => setOutorgado2((p) => ({ ...p, complemento: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Bairro" value={outorgado2.bairro} onChange={(e) => setOutorgado2((p) => ({ ...p, bairro: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField fullWidth label="Cidade" value={outorgado2.cidade} onChange={(e) => setOutorgado2((p) => ({ ...p, cidade: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField fullWidth label="Estado (UF)" value={outorgado2.estado} onChange={(e) => setOutorgado2((p) => ({ ...p, estado: e.target.value.toUpperCase() }))} inputProps={{ maxLength: 2 }} />
                </Grid>
              </>
            )}

            {/* Data */}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="date"
                label="Data"
                value={procuracaoData.data_procuracao}
                onChange={(e) => setProcuracaoData((prev) => ({ ...prev, data_procuracao: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProcuracao(false)}>Cancelar</Button>
          <Button onClick={handleGerarProcuracao} variant="contained" color="warning" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <PdfIcon />}>
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRecibo} onClose={() => setOpenRecibo(false)} maxWidth="md" fullWidth>
        <DialogTitle>Gerar Recibo por Venda</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                options={vendas}
                value={selectedVendaRecibo}
                onChange={(event, newValue) => setSelectedVendaRecibo(newValue)}
                getOptionLabel={(option) => {
                  const cliente = option.cliente?.nome || 'Cliente';
                  const veiculo = option.veiculo
                    ? `${option.veiculo.marca || ''} ${option.veiculo.modelo || ''} ${option.veiculo.placa ? `- ${option.veiculo.placa}` : ''}`.trim()
                    : 'Veículo';
                  return `#${option.id} - ${cliente} - ${veiculo}`;
                }}
                renderInput={(params) => <TextField {...params} label="Venda já realizada *" fullWidth />}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRecibo(false)}>Cancelar</Button>
          <Button onClick={handleGerarRecibo} variant="contained" color="success" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : <PdfIcon />}>
            Gerar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

